import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './Dashboard.css';
import { Chart, LinearScale, CategoryScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';
import { Line } from 'react-chartjs-2';
import annotationPlugin from 'chartjs-plugin-annotation';

// Register the components
Chart.register(
  LinearScale,
  CategoryScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  annotationPlugin
);

const Dashboard = ({ userData }) => {
  const [paidStats, setPaidStats] = useState({ fullPay: 0, halfPay: 0, noPay: 0 });
  const [demoStats, setDemoStats] = useState({ enrolled: 0, pending: 0, other: 0 });
  const [sessions, setSessions] = useState([]);
  const [filteredSessions, setFilteredSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [monthChanging, setMonthChanging] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [demoRating, setDemoRating] = useState(0);
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [demoDetails, setDemoDetails] = useState(null);
  const [loadingDemoDetails, setLoadingDemoDetails] = useState(false);
  const [demoError, setDemoError] = useState(null);
  const [graphData, setGraphData] = useState(null);
  const [graphLoading, setGraphLoading] = useState(true);
  const [graphError, setGraphError] = useState(null);
  const [showAllTeachersModal, setShowAllTeachersModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Add this useEffect to fetch top teachers data
  const topTeachers = React.useMemo(() => {
    if (!sessions.length) return [];
    
    // Filter only paid sessions that are completed
    const paidSessions = sessions.filter(session => 
      session.studenttype === 'paid' && 
      session.ClassCompletionStatus?.toLowerCase().includes('completed')
    );
    
    // Group by teacher and count sessions
    const teacherMap = {};
    
    paidSessions.forEach(session => {
      const teacherName = session.TeacherFullName || 'Unknown Teacher';
      const teacherId = session.TeacherId || teacherName;
      
      if (!teacherMap[teacherId]) {
        teacherMap[teacherId] = {
          teacherId,
          teacherName,
          sessionCount: 0
        };
      }
      
      teacherMap[teacherId].sessionCount++;
    });
    
    // Convert to array and sort by session count (descending)
    const teachersArray = Object.values(teacherMap);
    return teachersArray.sort((a, b) => b.sessionCount - a.sessionCount);
  }, [sessions]);
  
  const isNextMonthFuture = () => {
    const currentDate = new Date();
    const nextMonth = new Date(selectedYear, selectedMonth + 1, 1);
    return nextMonth > currentDate;
  };

  const formatToLocalDateTime = (isoString) => {
    if (!isoString) return { formattedDate: 'N/A', formattedTime: 'N/A' };
    
    try {
      const date = new Date(isoString);
      
      // Add the timezone offset to ensure correct local time
      const localDate = new Date(date.getTime());
      
      // Format for date: e.g., "Apr 26, 2025"
      const options  = localDate.toLocaleDateString([], { 
        weekday: 'short', // Sat
        day: '2-digit',   // 12
        month: 'short',   // Apr
        year: 'numeric'   // 2025 
      });
      const formattedDate = localDate.toLocaleDateString('en-GB', options).replace(/,/g, '');

      // Format for time: e.g., "1:00 PM"
      const formattedTime = localDate.toLocaleTimeString([], { 
        hour: '2-digit', 
        minute: '2-digit', 
        hour12: true 
      });
  
      return { formattedDate, formattedTime };
    } catch (error) {
      console.error('Error formatting date:', error);
      return { formattedDate: 'N/A', formattedTime: 'N/A' };
    }
  };

  useEffect(() => {
    const fetchGraphData = async () => {
      try {
        setGraphLoading(true);
        setGraphError(null);
        
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/admin/dashboard/statisticsgraph`, 
          {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('userData')}` }
          }
        );
  
        if (response.status !== 200) {
          throw new Error(`API returned status ${response.status}`);
        }
  
        setGraphData(response.data.data);
      } catch (err) {
        console.error('Error fetching graph data:', err);
        setGraphError(err.response?.data?.message || err.message || 'Failed to fetch graph data');
      } finally {
        setGraphLoading(false);
      }
    };
  
    if (userData?.id) fetchGraphData();
  }, [userData]);

  const prepareChartData = () => {
    if (!graphData) return null;
  
    // Sort data by year and month
    const sortedData = [...graphData].sort((a, b) => {
      if (a.year !== b.year) return a.year - b.year;
      return a.month - b.month;
    });
  
    // Get current month and year
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth() + 1;
    const currentYear = currentDate.getFullYear();
  
    // Format labels as "MMM YYYY"
    const labels = sortedData.map(item => {
      const date = new Date(item.year, item.month - 1);
      return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    });
  
    // Prepare dataset for upcoming sessions
    const upcomingSessionsData = sortedData.map(item => {
      // For current month, show upcoming + done
      if (item.year === currentYear && item.month === currentMonth) {
        return item.nbsessionsupcoming + item.nbsessionsdone;
      }
      // For future months, show just upcoming
      else if (item.year > currentYear || (item.year === currentYear && item.month > currentMonth)) {
        return item.nbsessionsupcoming;
      }
      // For past months, show nothing (null)
      return null;
    });
  
    return {
      labels,
      datasets: [
        {
          label: 'Completed Sessions',
          data: sortedData.map(item => item.nbsessionsdone),
          borderColor: 'rgb(75, 192, 192)',
          backgroundColor: 'rgba(75, 192, 192, 0.5)',
          tension: 0.3,
          yAxisID: 'y',
        },
        {
          label: 'Number of Students',
          data: sortedData.map(item => item.nbofstudents),
          borderColor: 'rgb(54, 162, 235)',
          backgroundColor: 'rgba(54, 162, 235, 0.5)',
          tension: 0.3,
          yAxisID: 'y1',
        },
        {
          label: 'Upcoming Sessions (including current)',
          data: upcomingSessionsData,
          borderColor: 'rgb(255, 159, 64)',
          backgroundColor: 'rgba(255, 159, 64, 0.5)',
          tension: 0.3,
          yAxisID: 'y',
          borderDash: [5, 5],
          fill: {
            target: '0',
            above: 'rgba(255, 159, 64, 0.1)',
          },
        }
      ]
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top',
      },
      tooltip: {
        callbacks: {
          afterBody: (context) => {
            const currentDate = new Date();
            const currentMonth = currentDate.getMonth() + 1;
            const currentYear = currentDate.getFullYear();
            const label = context[0].label;
            
            // Extract month and year from label (format: "MMM YYYY")
            const [monthStr, yearStr] = label.split(' ');
            const date = new Date(`${monthStr} 1, ${yearStr}`);
            const month = date.getMonth() + 1;
            const year = date.getFullYear();
            
            if (year === currentYear && month === currentMonth) {
              return [''];
            }
            return [];
          }
        }
      },
      annotation: {
        annotations: {
          currentMonthLine: {
            type: 'line',
            scaleID: 'x',
            value: (ctx) => {
              if (!graphData) return null;
              const currentDate = new Date();
              const currentMonth = currentDate.getMonth() + 1;
              const currentYear = currentDate.getFullYear();
              
              const sortedData = [...graphData].sort((a, b) => {
                if (a.year !== b.year) return a.year - b.year;
                return a.month - b.month;
              });
              
              const currentIndex = sortedData.findIndex(item => 
                item.year === currentYear && item.month === currentMonth
              );
              
              return currentIndex >= 0 ? currentIndex : null;
            },
            borderColor: 'rgb(255, 99, 132)',
            borderWidth: 2,
            borderDash: [6, 6],
            label: {
              content: 'Current Month',
              enabled: true,
              position: 'top',
              backgroundColor: 'rgba(255, 99, 132, 0.7)',
              color: '#fff',
              font: {
                size: 12
              }
            }
          }
        }
      }
    },
    scales: {
      y: {
        type: 'linear',
        display: true,
        position: 'right',
        title: {
          display: true,
          text: 'Number of Sessions',
        },
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'left',
        title: {
          display: true,
          text: 'Number of Students',
        },
        grid: {
          drawOnChartArea: false,
        },
      },
    },
  };

  const fetchDemoDetails = async (demoId) => {
    try {
      setLoadingDemoDetails(true);
      setDemoError(null);
      
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/admin/dashboard/demosubmit/${demoId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('userData')}` }
      });

      if (response.status !== 200) {
        throw new Error(`API returned status ${response.status}`);
      }

      setDemoDetails(response.data);
      setShowDemoModal(true);
    } catch (err) {
      console.error('Error fetching demo details:', err);
      setDemoError(err.response?.data?.message || err.message || 'Failed to fetch demo details');
    } finally {
      setLoadingDemoDetails(false);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!userData?.id) {
        throw new Error('User data not available');
      }

      const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/api/admin/dashboard/statistics/${selectedMonth + 1}/${selectedYear}`;
      
      const response = await axios.get(apiUrl, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('userData')}` }
      });

      if (response.status !== 200) {
        throw new Error(`API returned status ${response.status}`);
      }

      const apiData = response.data;
      
      // Paid statistics
      setPaidStats({
        fullPay: apiData.fullPayCount || 0,
        halfPay: apiData.halfPayCount || 0,
        noPay: apiData.noPayCount || 0
      });
      
      // Demo statistics
      setDemoStats({
        enrolled: apiData.enrolledStudents || 0,
        pending: apiData.pendingStudents || 0,
        other: (apiData.registrationPostponedStudents || 0) + 
               (apiData.notInterestedCount || 0) + 
               (apiData.awaiting || 0) + 
               (apiData.caseClosedStudents || 0)
      });
      
      const allSessions = apiData.allSessions || [];
      setSessions(allSessions);
      setFilteredSessions(allSessions);

      // Calculate demo rating
      const demoEnrolled = apiData.enrolledStudents || 0;
      const demoTotal = allSessions.filter(s => s.studenttype === 'demo').length;
      const rating = demoTotal > 0 ? Math.round((demoEnrolled / demoTotal) * 100) : 0;
      setDemoRating(rating);
    } catch (err) {
      console.error('API Error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
      setMonthChanging(false);
    }
  };

  const changeMonth = (direction) => {
    if (direction === 'next' && isNextMonthFuture()) return;
    
    setMonthChanging(true);
    const newDate = new Date(selectedYear, selectedMonth, 1);
    newDate.setMonth(direction === 'prev' ? newDate.getMonth() - 1 : newDate.getMonth() + 1);
    
    setSelectedMonth(newDate.getMonth());
    setSelectedYear(newDate.getFullYear());
  };

  useEffect(() => {
    if (userData?.id) fetchData();
  }, [userData, selectedMonth, selectedYear]);

  useEffect(() => {
    let results = sessions;
    
    // Apply search filter
    if (searchTerm) {
      results = results.filter(session => 
        session.StudentFullName.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply type filter
    if (filterType !== 'all') {
      results = results.filter(session => session.studenttype === filterType);
    }
    
    setFilteredSessions(results);
  }, [searchTerm, filterType, sessions]);

  const monthYearDisplay = new Date(selectedYear, selectedMonth).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric'
  });

  const getStatusColor = (status) => {
    if(!status) return 'text-red';

    const statusLower = status.toLowerCase();
    if (statusLower.includes('full pay') || statusLower.includes('enrolled')) {
      return 'text-green';
    }
    if (statusLower.includes('half pay') || statusLower.includes('postponed')) {
      return 'text-orange';
    }
    if (statusLower.includes('pending')) {
      return 'text-blue';
    }
    if (statusLower.includes('awaiting')) {
      return 'text-grey';
    }
    return 'text-red';
  };

  if (loading && !paidStats.fullPay) {
    return (
      <div className="sales-dashboard">
        <div className="sales-dashboard__loading-container">
          <div className="sales-dashboard__loading-spinner"></div>
          <p>Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="sales-dashboard">
        <div className="sales-dashboard__error-container">
          <div className="sales-dashboard__error-message">{error}</div>
          <button 
            className="sales-dashboard__retry-button"
            onClick={() => fetchData()}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="sales-dashboard">
      {/* Header */}
      <header className="sales-dashboard__header-compact">
        <div>
          <h1 className="sales-dashboard__header-title">
            <span className="sales-dashboard__header-greeting">Welcome back,</span>
            <span className="sales-dashboard__header-name">{userData?.fullName || 'Admin'}</span>
          </h1>
          <p className="sales-dashboard__header-subtitle">Admin Dashboard</p>
        </div>
        <div className="sales-dashboard__header-actions">
          <div className="sales-dashboard__current-period">
            <span className="sales-dashboard__period-label">Viewing:</span>
            <span className="sales-dashboard__period-value">{monthYearDisplay}</span>
          </div>
        </div>
      </header>
           
      {/* Graph Statistics Section */}
      <section className="sales-dashboard__graph-compact">
        <div className="sales-dashboard__graph-header">
          <h3 className="sales-dashboard__graph-title">Monthly Overview</h3>
        </div>
        
        {graphLoading ? (
          <div className="sales-dashboard__graph-loading-compact">
            <div className="sales-dashboard__loading-spinner"></div>
            <p>Loading analytics...</p>
          </div>
        ) : graphError ? (
          <div className="sales-dashboard__graph-error-compact">
            <div className="sales-dashboard__error-icon">⚠️</div>
            <div>
              <p className="sales-dashboard__error-message">Failed to load analytics</p>
              <button 
                className="sales-dashboard__error-retry"
                onClick={() => window.location.reload()}
              >
                Retry
              </button>
            </div>
          </div>
        ) : graphData ? (
          <div className="sales-dashboard__graph-wrapper">
            <Line data={prepareChartData()} options={chartOptions} />
          </div>
        ) : (
          <div className="sales-dashboard__graph-empty">
            <div className="sales-dashboard__empty-icon">📊</div>
            <p>No analytics data available</p>
          </div>
        )}
      </section>
      
      {/* Month Navigation */}
      <section className="sales-dashboard__month-nav-compact">
        <div className="sales-dashboard__month-nav-wrapper">
          <button 
            className="sales-dashboard__month-nav-btn sales-dashboard__month-nav-prev"
            onClick={() => changeMonth('prev')}
            disabled={monthChanging}
            title="Previous month"
          >
            ◀
          </button>
          
          <div className="sales-dashboard__month-display-compact">
            <span className="sales-dashboard__month-name">
              {new Date(selectedYear, selectedMonth).toLocaleDateString('en-US', { month: 'long' })}
            </span>
            <span className="sales-dashboard__month-year">{selectedYear}</span>
          </div>
          
          <button 
            className="sales-dashboard__month-nav-btn sales-dashboard__month-nav-next"
            onClick={() => changeMonth('next')}
            disabled={isNextMonthFuture() || monthChanging}
            title="Next month"
          >
            ▶
          </button>
          
          <div className="sales-dashboard__month-quick-nav">
            <button 
              className="sales-dashboard__quick-nav-btn"
              onClick={() => {
                const currentDate = new Date();
                setSelectedMonth(currentDate.getMonth());
                setSelectedYear(currentDate.getFullYear());
              }}
            >
              Current Month
            </button>
          </div>
        </div>
      </section>
     
      {/* Top Teachers Section */}
      <section className="sales-dashboard__stats-section sales-dashboard__top-teachers">
        <div className="sales-dashboard__section-header">
          <h3 className="sales-dashboard__section-title">Top Teachers</h3>
          {topTeachers.length > 3 && (
            <button 
              className="sales-dashboard__view-all-link"
              onClick={() => setShowAllTeachersModal(true)}
            >
              View All ({topTeachers.length})
            </button>
          )}
        </div>
        
        {topTeachers.length > 0 ? (
          <div className="sales-dashboard__top-teachers-grid">
            {topTeachers.slice(0, 3).map((teacher, index) => (
              <div 
                key={teacher.teacherId || index} 
                className={`sales-dashboard__teacher-card ${index === 0 ? 'teacher-first' : index === 1 ? 'teacher-second' : 'teacher-third'}`}
              >
                <div className="sales-dashboard__teacher-medal">
                  {index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'}
                </div>
                <div className="sales-dashboard__teacher-info">
                  <h4 className="sales-dashboard__teacher-name">{teacher.teacherName}</h4>
                  <p className="sales-dashboard__teacher-sessions">{teacher.sessionCount} paid sessions</p>
                </div>
                <div className="sales-dashboard__teacher-rank-badge">
                  #{index + 1}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="sales-dashboard__no-teachers">
            No paid sessions completed this month
          </div>
        )}
      </section>

      {/* Combined Stats Section */}
      <section className="sales-dashboard__stats-section sales-dashboard__combined-stats">
        <h3 className="sales-dashboard__section-title">Monthly Statistics</h3>
        
        <div className="sales-dashboard__stats-compact-grid">
          {/* Paid Statistics */}
          <div className="sales-dashboard__stat-category">
            <h4 className="sales-dashboard__stat-category-title">
              <span className="sales-dashboard__stat-category-icon">💰</span>
              Paid Classes
            </h4>
            <div className="sales-dashboard__stat-row">
              <div className="sales-dashboard__stat-item sales-dashboard__stat-item-full">
                <span className="sales-dashboard__stat-label">Full Pay</span>
                <span className="sales-dashboard__stat-number">{paidStats.fullPay}</span>
              </div>
              <div className="sales-dashboard__stat-item sales-dashboard__stat-item-half">
                <span className="sales-dashboard__stat-label">Half Pay</span>
                <span className="sales-dashboard__stat-number">{paidStats.halfPay}</span>
              </div>
              <div className="sales-dashboard__stat-item sales-dashboard__stat-item-none">
                <span className="sales-dashboard__stat-label">No Pay</span>
                <span className="sales-dashboard__stat-number">{paidStats.noPay}</span>
              </div>
            </div>
          </div>
          
          {/* Demo Statistics */}
          <div className="sales-dashboard__stat-category">
            <h4 className="sales-dashboard__stat-category-title">
              <span className="sales-dashboard__stat-category-icon">🎯</span>
              Demo Classes
            </h4>
            <div className="sales-dashboard__stat-row">
              <div className="sales-dashboard__stat-item sales-dashboard__stat-item-enrolled">
                <span className="sales-dashboard__stat-label">Enrolled</span>
                <span className="sales-dashboard__stat-number">{demoStats.enrolled}</span>
              </div>
              <div className="sales-dashboard__stat-item sales-dashboard__stat-item-pending">
                <span className="sales-dashboard__stat-label">Pending</span>
                <span className="sales-dashboard__stat-number">{demoStats.pending}</span>
              </div>
              <div className="sales-dashboard__stat-item sales-dashboard__stat-item-other">
                <span className="sales-dashboard__stat-label">Other</span>
                <span className="sales-dashboard__stat-number">{demoStats.other}</span>
              </div>
            </div>
          </div>
          
          {/* Demo Conversion Rate */}
          <div className="sales-dashboard__stat-category">
            <h4 className="sales-dashboard__stat-category-title">
              <span className="sales-dashboard__stat-category-icon">📈</span>
              Conversion Rate
            </h4>
            <div className="sales-dashboard__conversion-wrapper">
              <div className="sales-dashboard__conversion-rate">
                <span className="sales-dashboard__rate-value">{demoRating}%</span>
                <span className="sales-dashboard__rate-label">Conversion</span>
              </div>
              <div className="sales-dashboard__conversion-details">
                <p className="sales-dashboard__conversion-text">
                  {demoStats.enrolled} enrolled out of {sessions.filter(s => s.studenttype === 'demo').length} demos
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Search and Filter */}
      <section className="sales-dashboard__search-section">
        <div className="sales-dashboard__search-header">
          <h3 className="sales-dashboard__section-title">Class Sessions - {monthYearDisplay}</h3>
          <div className="sales-dashboard__search-filters">
            <div className="sales-dashboard__search-wrapper">
              <span className="sales-dashboard__search-icon">🔍</span>
              <input
                type="text"
                placeholder="Search students..."
                className="sales-dashboard__search-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button 
                  className="sales-dashboard__search-clear"
                  onClick={() => setSearchTerm('')}
                >
                  ✕
                </button>
              )}
            </div>
            <div className="sales-dashboard__filter-wrapper">
              <span className="sales-dashboard__filter-icon">⚙️</span>
              <select
                className="sales-dashboard__filter-select"
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
              >
                <option value="all">All Types</option>
                <option value="upcoming">Upcoming</option>
                <option value="demo">Demo</option>
                <option value="paid">Paid</option>
              </select>
            </div>
            <div className="sales-dashboard__results-info">
              <span className="sales-dashboard__results-count">{filteredSessions.length}</span>
              <span className="sales-dashboard__results-label">sessions</span>
            </div>
          </div>
        </div>
      </section>

      {/* Classes Table */}
      <section className="sales-dashboard__classes-section">
        {filteredSessions.length > 0 ? (
          <div className="sales-dashboard__table-container">
            <table className="sales-dashboard__classes-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Student</th>
                  <th>Teacher</th>
                  <th>Date & Time</th>
                  <th>Status</th>
                  <th>Report</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredSessions.map((session, index) => (
                  <tr key={index}>
                    <td>
                      <div className={`sales-dashboard__type-tag sales-dashboard__type-${session.studenttype}`}>
                        <a 
                          href={`studentprofile/${session.IdUser}`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="sales-dashboard__type-link"
                        >
                          {session.studenttype}
                        </a>
                      </div>
                    </td>
                    <td>
                      <a 
                        href={`studentprofile/${session.IdUser}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="sales-dashboard__student-link"
                      >
                        <span className="sales-dashboard__student-name">{session.StudentFullName}</span>
                      </a>
                    </td>
                    <td>
                      <span className="sales-dashboard__teacher-text">{session.TeacherFullName}</span>
                    </td>
                    <td>
                      <div className="sales-dashboard__datetime-cell">
                        <div className="sales-dashboard__date-cell">{formatToLocalDateTime(session.ClassDateTime).formattedDate}</div>
                        <div className="sales-dashboard__time-cell">{formatToLocalDateTime(session.ClassDateTime).formattedTime}</div>
                      </div>
                    </td>
                    <td>
                      <span className={`sales-dashboard__status-tag ${
                        session.ClassCompletionStatus?.toLowerCase().includes('completed') ? 'status-completed' : 
                        session.ClassCompletionStatus?.toLowerCase().includes('scheduled') ? 'status-scheduled' : 
                        'status-pending'
                      }`}>
                        {session.ClassCompletionStatus}
                      </span>
                    </td>
                    <td>
                      <span className={`sales-dashboard__report-tag sales-dashboard__${getStatusColor(session.ClassStatus).replace('text-', '')}`}>
                        {session.ClassStatus}
                      </span>
                    </td>
                    <td>
                      <div className="sales-dashboard__action-cell">
                        {session.studenttype !== 'paid' && session.MobileNumber && (
                          <a 
                            href={`https://wa.me/${session.MobileNumber}`} 
                            target="_blank" 
                            rel="noreferrer" 
                            className="sales-dashboard__action-whatsapp"
                            title="WhatsApp"
                          >
                            <span className="sales-dashboard__action-icon">💬</span>
                            <span className="sales-dashboard__action-text">WhatsApp</span>
                          </a>
                        )}
                        {session.studenttype === 'demo' && (
                          <button 
                            onClick={() => fetchDemoDetails(session.DemoId)} 
                            className="sales-dashboard__action-details"
                            disabled={loadingDemoDetails}
                            title="View Details"
                          >
                            <span className="sales-dashboard__action-icon">👁️</span>
                            <span className="sales-dashboard__action-text">Details</span>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="sales-dashboard__no-data">
            <div className="sales-dashboard__no-data-icon">📭</div>
            <p className="sales-dashboard__no-data-text">No matching classes found</p>
            <p className="sales-dashboard__no-data-subtext">Try adjusting your search or filter criteria</p>
          </div>
        )}
      </section>
      
      {/* All Teachers Modal */}
      {showAllTeachersModal && (
        <div className="sales-dashboard__modal-overlay">
          <div className="sales-dashboard__all-teachers-modal">
            <div className="sales-dashboard__modal-header">
              <h3>All Teachers - {monthYearDisplay}</h3>
              <button 
                className="sales-dashboard__modal-close"
                onClick={() => setShowAllTeachersModal(false)}
              >
                &times;
              </button>
            </div>
            
            <div className="sales-dashboard__modal-content">
              <div className="sales-dashboard__teachers-summary">
                <div className="sales-dashboard__summary-item">
                  <span className="sales-dashboard__summary-label">Total Teachers:</span>
                  <span className="sales-dashboard__summary-value">{topTeachers.length}</span>
                </div>
                <div className="sales-dashboard__summary-item">
                  <span className="sales-dashboard__summary-label">Total Sessions:</span>
                  <span className="sales-dashboard__summary-value">
                    {topTeachers.reduce((total, teacher) => total + teacher.sessionCount, 0)}
                  </span>
                </div>
                <div className="sales-dashboard__summary-item">
                  <span className="sales-dashboard__summary-label">Average Sessions:</span>
                  <span className="sales-dashboard__summary-value">
                    {topTeachers.length > 0 
                      ? Math.round(topTeachers.reduce((total, teacher) => total + teacher.sessionCount, 0) / topTeachers.length)
                      : 0
                    }
                  </span>
                </div>
              </div>
              
              <div className="sales-dashboard__teachers-table-container">
                <table className="sales-dashboard__teachers-table">
                  <thead>
                    <tr>
                      <th>Rank</th>
                      <th>Teacher Name</th>
                      <th>Sessions</th>
                      <th>Percentage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topTeachers.map((teacher, index) => {
                      const totalSessions = topTeachers.reduce((sum, t) => sum + t.sessionCount, 0);
                      const percentage = totalSessions > 0 
                        ? ((teacher.sessionCount / totalSessions) * 100).toFixed(1)
                        : 0;
                      
                      return (
                        <tr key={teacher.teacherId || index}>
                          <td className="sales-dashboard__teacher-rank-cell">
                            <span className={`sales-dashboard__rank-badge ${index < 3 ? `rank-${index + 1}` : ''}`}>
                              {index + 1}
                            </span>
                          </td>
                          <td className="sales-dashboard__teacher-name-cell">
                            <div className="sales-dashboard__teacher-name-with-medal">
                              {index < 3 && (
                                <span className="sales-dashboard__table-medal">
                                  {index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'}
                                </span>
                              )}
                              {teacher.teacherName}
                            </div>
                          </td>
                          <td className="sales-dashboard__teacher-sessions-cell">
                            {teacher.sessionCount}
                          </td>
                          <td className="sales-dashboard__teacher-percentage-cell">
                            <div className="sales-dashboard__percentage-bar-container">
                              <div 
                                className="sales-dashboard__percentage-bar"
                                style={{ width: `${percentage}%` }}
                              ></div>
                              <span className="sales-dashboard__percentage-text">{percentage}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              
              <div className="sales-dashboard__modal-actions">
                <button
                  className="sales-dashboard__modal-cancel"
                  onClick={() => setShowAllTeachersModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDemoModal && (
        <div className="sales-dashboard__modal-overlay">
          <div className="sales-dashboard__modal">
            <div className="sales-dashboard__modal-header">
              <h3>Demo Class Details</h3>
              <button 
                className="sales-dashboard__modal-close"
                onClick={() => {
                  setShowDemoModal(false);
                  setDemoDetails(null);
                }}
              >
                &times;
              </button>
            </div>

            {demoError ? (
              <div className="sales-dashboard__modal-error">
                {demoError}
                <button 
                  className="sales-dashboard__retry-button"
                  onClick={() => demoDetails && fetchDemoDetails(demoDetails.id)}
                >
                  Try Again
                </button>
              </div>
            ) : demoDetails ? (
              <div className="sales-dashboard__modal-content">
                <div className="sales-dashboard__modal-row">
                  <span className="sales-dashboard__modal-label">Student Name:</span>
                  <span className="sales-dashboard__modal-value">{demoDetails.studentName}</span>
                </div>

                <div className="sales-dashboard__modal-row">
                  <span className="sales-dashboard__modal-label">Class Date & Time:</span>
                  <span className="sales-dashboard__modal-value">
                    {formatToLocalDateTime(demoDetails.classDateTime).formattedDate}{" "}
                    {formatToLocalDateTime(demoDetails.classDateTime).formattedTime}
                  </span>
                </div>

                <div className="sales-dashboard__modal-row">
                  <span className="sales-dashboard__modal-label">Project Name:</span>
                  <select
                    className="sales-dashboard__modal-input"
                    value={demoDetails.projectId || ""}
                    onChange={(e) =>
                      setDemoDetails({ ...demoDetails, projectId: parseInt(e.target.value) })
                    }
                  >
                    <option value="">Select a project</option>
                    {demoDetails.projects?.map((project) => (
                      <option key={project.Id} value={project.Id}>
                        {project.ProjectName}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="sales-dashboard__modal-row">
                  <span className="sales-dashboard__modal-label">Class Rating:</span>
                  <select
                    className="sales-dashboard__modal-input"
                    value={demoDetails.rating}
                    onChange={(e) => setDemoDetails({ ...demoDetails, rating: e.target.value })}
                  >
                    <option value="0">No Rating</option>
                    <option value="1">1 - Poor</option>
                    <option value="2">2 - Fair</option>
                    <option value="3">3 - Good</option>
                    <option value="4">4 - Very Good</option>
                    <option value="5">5 - Excellent</option>
                  </select>
                </div>

                <div className="sales-dashboard__modal-row">
                  <span className="sales-dashboard__modal-label">Contacted Parents:</span>
                  <select
                    className="sales-dashboard__modal-input"
                    value={demoDetails.parentsContacted}
                    onChange={(e) => setDemoDetails({ ...demoDetails, parentsContacted: e.target.value })}
                  >
                    <option value="1">Yes</option>
                    <option value="0">No</option>
                  </select>
                </div>

                <div className="sales-dashboard__modal-row">
                  <span className="sales-dashboard__modal-label">Class Status:</span>
                  <select
                    className="sales-dashboard__modal-input"
                    value={demoDetails.classStatus || ""}
                    onChange={(e) => setDemoDetails({ ...demoDetails, classStatus: e.target.value })}
                  >
                    <option value="0">Class Completed</option>
                    <option value="1">Student Not Joined</option>
                    <option value="2">Student Left</option>
                    <option value="3">Student Not Interested</option>
                  </select>
                </div>

                <div className="sales-dashboard__modal-row">
                  <span className="sales-dashboard__modal-label">Details:</span>
                  <textarea
                    className="sales-dashboard__modal-textarea"
                    value={demoDetails.details || ""}
                    onChange={(e) => setDemoDetails({ ...demoDetails, details: e.target.value })}
                    rows="3"
                  />
                </div>

                <div className="sales-dashboard__modal-row">
                  <span className="sales-dashboard__modal-label">Class Report:</span>
                  <select
                    className="sales-dashboard__modal-input"
                    value={demoDetails.classReport || 0}
                    onChange={(e) => setDemoDetails({ ...demoDetails, classReport: parseInt(e.target.value) })}
                  >
                    <option value="0">Awaiting</option>
                    <option value="1">Enrolled</option>
                    <option value="2">Pending</option>
                    <option value="3">Closed</option>
                    <option value="4">Postponed</option>
                    <option value="5">Not Interested</option>
                  </select>
                </div>

                <div className="sales-dashboard__modal-actions">
                  <button
                    className="sales-dashboard__modal-save"
                    onClick={async () => {
                      try {
                        await axios.put(
                          `${process.env.REACT_APP_API_BASE_URL}/api/admin/dashboard/demosubmit/${demoDetails.id}`,
                          {
                            projectId: demoDetails.projectId,
                            rating: demoDetails.rating,
                            parentsAvailable: demoDetails.parentsContacted,
                            classStatus: demoDetails.classStatus,
                            details: demoDetails.details,
                            classReport: demoDetails.classReport
                          },
                          {
                            headers: {
                              Authorization: `Bearer ${localStorage.getItem("userData")}`
                            }
                          }
                        );
                        setShowDemoModal(false);
                        fetchData();
                      } catch (error) {
                        console.error("Error updating demo:", error);
                        setDemoError(error.response?.data?.message || "Failed to update demo");
                      }
                    }}
                  >
                    Save Changes
                  </button>
                  
                  <button
                    className="sales-dashboard__modal-delete"
                    onClick={() => setShowDeleteConfirm(true)}
                  >
                    Delete
                  </button>
                  
                  <button
                    className="sales-dashboard__modal-cancel"
                    onClick={() => setShowDemoModal(false)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="sales-dashboard__modal-loading">
                <div className="sales-dashboard__loading-spinner small"></div>
                <p>Loading demo details...</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Delete Confirmation Popup */}
      {showDeleteConfirm && (
        <div className="sales-dashboard__confirm-overlay">
          <div className="sales-dashboard__confirm-modal">
            <div className="sales-dashboard__confirm-header">
              <h3>Confirm Deletion</h3>
            </div>
            
            <div className="sales-dashboard__confirm-content">
              <p>Are you sure you want to delete this demo class? This action cannot be undone.</p>
            </div>
            
            <div className="sales-dashboard__confirm-actions">
              <button
                className="sales-dashboard__confirm-cancel"
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </button>
              <button
                className="sales-dashboard__confirm-delete"
                onClick={async () => {
                  try {
                    await axios.delete(
                      `${process.env.REACT_APP_API_BASE_URL}/api/admin/dashboard/demosubmit/${demoDetails.id}`,
                      {
                        headers: {
                          Authorization: `Bearer ${localStorage.getItem("userData")}`
                        }
                      }
                    );
                    setShowDeleteConfirm(false);
                    setShowDemoModal(false);
                    fetchData();
                  } catch (error) {
                    console.error("Error deleting demo:", error);
                    setDemoError(error.response?.data?.message || "Failed to delete demo");
                    setShowDeleteConfirm(false);
                  }
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;